import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { findEmployees, type Employee } from '../../../api/employees';
import { toUserMessage } from '../../../api/client';
import { formatFullName } from '../../../utils/formatFullName';
import { Avatar } from '../Avatar';
import { Button } from '../Button';
import { Dropdown } from '../Dropdown';

const ITEMS_PER_PAGE = 20;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
`;

const ListItem = styled.li<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${({ theme }) => theme.space[1]}px ${({ theme }) => theme.space[2]}px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.highlight.blue25 : 'transparent'};
  color: ${({ $selected, theme }) =>
    $selected ? theme.colors.primary.main : theme.colors.text.primary};
  transition: background 150ms;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.highlight.blue25 : theme.colors.highlight.gray25};
  }
`;

const EmptyMessage = styled.div`
  padding: ${({ theme }) => theme.space[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
`;

const LoadingMessage = styled.div`
  padding: ${({ theme }) => theme.space[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
`;

const LoadMoreSentinel = styled.div`
  height: 1px;
  visibility: hidden;
`;

function getEmployeeDisplayName(emp: Employee): string {
  const fullName = formatFullName(emp.familyName, emp.givenName, emp.patronymic);
  return fullName || emp.email || emp.id;
}

export interface EmployeeSelectProps {
  value?: Employee | null;
  onChange?: (employee: Employee | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function EmployeeSelect({
  value,
  onChange,
  placeholder = 'Выберите сотрудника',
  disabled = false,
}: EmployeeSelectProps) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Employee[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const hasMore = items.length < totalCount;

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPage(1);
    try {
      const data = await findEmployees({ page: 1, itemsPerPage: ITEMS_PER_PAGE });
      setItems(data.items);
      setTotalCount(data.count);
    } catch (err) {
      setError(toUserMessage(err));
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const data = await findEmployees({
        page: nextPage,
        itemsPerPage: ITEMS_PER_PAGE,
      });
      setItems((prev) => [...prev, ...data.items]);
      setPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore]);

  useEffect(() => {
    if (open) {
      loadInitial();
    }
  }, [open, loadInitial]);

  useEffect(() => {
    if (!open || !hasMore || loading || loadingMore) return;

    const list = listRef.current;
    const sentinel = sentinelRef.current;
    if (!list || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          void loadMore();
        }
      },
      { root: list, rootMargin: '80px', threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, loading, loadingMore, loadMore]);

  const handleSelect = useCallback(
    (emp: Employee) => {
      onChange?.(emp);
      setOpen(false);
    },
    [onChange],
  );

  const triggerLabel = value ? getEmployeeDisplayName(value) : placeholder;

  return (
    <Dropdown
      trigger={
        <Button variant="outlined" color="primary" disabled={disabled}>
          {triggerLabel}
        </Button>
      }
      open={open}
      onOpenChange={setOpen}
      width="280px"
    >
      {loading && <LoadingMessage>Загрузка...</LoadingMessage>}
      {error && <EmptyMessage role="alert">{error}</EmptyMessage>}
      {!loading && !error && items.length === 0 && (
        <EmptyMessage>Нет сотрудников</EmptyMessage>
      )}
      {!loading && !error && items.length > 0 && (
        <List ref={listRef} role="listbox">
          {items.map((emp) => (
            <ListItem
              key={emp.id}
              $selected={value?.id === emp.id}
              role="option"
              onClick={() => handleSelect(emp)}
            >
              <Avatar
                url={emp.avatarUrl}
                letter={getEmployeeDisplayName(emp)}
                size="xxs"
                alt={getEmployeeDisplayName(emp)}
              />
              <span>
                {getEmployeeDisplayName(emp)}
                {emp.email && (
                  <span style={{ opacity: 0.8, fontSize: '0.9em' }}>
                    {' '}
                    — {emp.email}
                  </span>
                )}
              </span>
            </ListItem>
          ))}
          {hasMore && <LoadMoreSentinel ref={sentinelRef} data-sentinel />}
          {loadingMore && <LoadingMessage>Загрузка…</LoadingMessage>}
        </List>
      )}
    </Dropdown>
  );
}
